import {
  BorderBox,
  Box,
  Divider,
  FlexBox,
  Grid,
  Text,
  TextInline,
} from '@trmediaab/zebra';
import { formatNumber } from '@trmediaab/zebra-utils';
import { Fragment } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { selectPremium } from '~/containers/Auth/selectors';
import { spacing } from '~/utils/css-mixins';
import { parseBridle, parseCarriage, parseShoes } from '~/utils/equipment';
import { formatDate } from '~/utils/format';
import { formatOdds, formatSpeed } from '~/utils/numbers';
import { parsePlacement } from '~/utils/race';

import { Carriage } from './Carriage';
import { Shoes } from './Shoes';

const SPACER = [
  {
    id: 'spacer',
    columnWidth: 'minmax(auto, 45px)',
  },
];

const getColumns = start => [
  {
    id: 'date',
    columnWidth: '50px',
    value: (
      <Text fontSize="1" fontWeight="bold">
        {formatDate(start.race.meet.race_date, 'yyMMdd')}
      </Text>
    ),
  },
  ...SPACER,
  {
    id: 'raceInfo',
    columnWidth: '40px',
    value: (
      <Text fontSize="1">
        {start.race.meet.track.id}-{start.race.race_number}
      </Text>
    ),
  },
  ...SPACER,
  {
    id: 'driver',
    columnWidth: '50px',
    value: (
      <Text fontSize="1" fontWeight="bold">
        {start.driver ? start.driver.short_name : '-'}
      </Text>
    ),
  },
  ...SPACER,
  {
    id: 'startPosition',
    columnWidth: '55px',
    value: (
      <Text fontSize="1" fontWeight="bold">
        {start.race.base_dist}
        {start.race.start_method_id === 'A' ? 'a' : ''}:{start.start_position}
      </Text>
    ),
  },
  ...SPACER,
  {
    id: 'placement',
    columnWidth: '26px',
    value: (
      <BorderBox
        borderRadius="circle"
        width="26px"
        height="26px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        backgroundColor="brands.travrondenSpel"
      >
        <Text fontSize="1" fontWeight="bold" color="white">
          {parsePlacement(start).text}
        </Text>
      </BorderBox>
    ),
  },
  ...SPACER,
  {
    id: 'kmSpeed',
    columnWidth: '35px',
    value: (
      <Text fontSize="1" fontWeight="bold">
        {formatSpeed(start.result.km_speed)}
      </Text>
    ),
  },
  ...SPACER,
  {
    id: 'firstPrize',
    columnWidth: '28px',
    value: (
      <Text fontSize="1">{formatNumber(start.race.first_prize / 1000)}´</Text>
    ),
  },
  ...SPACER,
  {
    id: 'odds',
    columnWidth: 'max-content',
    value: (
      <Text fontSize="1" fontWeight="bold">
        {formatOdds(start.odds.providers.ATG?.V)}
      </Text>
    ),
  },
];

const PreviousStarts = ({ starts, access }) => {
  const latestFiveStarts = starts.slice(0, 5);

  return latestFiveStarts.map((start, idx) => {
    const columns = getColumns(start);
    const gridTemplateColumns = columns
      .map(({ columnWidth }) => columnWidth)
      .join(' ');

    return (
      <Fragment key={start.id}>
        {idx > 0 && <Divider variant="startlist" mx="-16px" />}

        <FlexBox
          flexDirection="column"
          gap="2"
          pb="1"
          maxWidth={spacing.siteContentWidth}
        >
          <Grid gridTemplateColumns={gridTemplateColumns}>
            {columns.map(({ id, value }, idx) =>
              id === 'spacer' ? (
                <Box key={idx} />
              ) : (
                <Fragment key={idx}>{value}</Fragment>
              ),
            )}
          </Grid>

          {start.comment != null && (
            <Text
              fontSize="1"
              fontWeight="semiBold"
              fontStyle="italic"
              color="greys.3"
              pb="1"
            >
              {access
                ? start.comment
                : 'Loppkommentar ingår som en tjänst i Travronden Digital.'}
            </Text>
          )}

          <FlexBox gap="2">
            <Box flex="1">
              {start.race.top3.map((top, i) => (
                <Text fontSize="1" fontWeight="semiBold" key={i}>
                  {top.placement} {top.horse_name} {formatSpeed(top.km_speed)}
                </Text>
              ))}
            </Box>

            <Box flex="1">
              <FlexBox gap="2">
                <Carriage
                  fontSize="1"
                  fontWeight="bold"
                  data={parseCarriage(start)}
                />

                <Shoes
                  fontSize="1"
                  fontWeight="bold"
                  data={parseShoes(start)}
                />
              </FlexBox>

              <Text fontSize="1">{parseBridle(start).value ?? '- -'}</Text>
              {start.race.intermediate_times != null && (
                <Text fontSize="1" fontWeight="medium" whiteSpace="nowrap">
                  <TextInline>
                    t:{' '}
                    {start.race.intermediate_times
                      .map(time => formatSpeed(time))
                      .join('-')}
                  </TextInline>
                </Text>
              )}
            </Box>
          </FlexBox>
        </FlexBox>
      </Fragment>
    );
  });
};

export default connect(
  createStructuredSelector({
    access: selectPremium,
  }),
)(PreviousStarts);
